import { useState, useEffect, useMemo, lazy, Suspense } from 'react';
import './App.css';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ToastProvider } from './components/ui/Toast';
import { ErrorBoundary } from './components/ErrorBoundary';
import SSORedirect from './components/SSORedirect';
import TransactionAnalyzer from './components/TransactionAnalyzer';
import type { TransactionData, MarketplaceCode } from './types/transaction';
import { fetchLiveRates, isSupportedMarketplace } from './utils/currencyExchange';
import { fetchTrendData } from './services/api/configApi';
import { apiToTransactionData } from './utils/transactionStorage';
import { fetchProductMapping, createProductMap, enrichTransaction } from './services/productMapping';
import { logger } from './utils/logger';

// Lazy load heavy components - only loaded when user navigates to them
const ProfitabilityAnalyzer = lazy(() => import('./components/ProfitabilityAnalyzer'));
const TrendsAnalyzer = lazy(() => import('./components/TrendsAnalyzer'));
const QuickProfit = lazy(() => import('./components/QuickProfit'));
const TestPanel = lazy(() => import('./shared/testing/TestPanel'));

// Loading fallback component
const LoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100">
    <div className="text-center" role="status" aria-live="polite">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4" aria-hidden="true"></div>
      <p className="text-slate-600">Loading...</p>
    </div>
  </div>
);

type Phase = 'transaction' | 'profitability' | 'quickprofit' | 'trends';

// Main App Content (requires authentication)
function AppContent() {
  const { user, logout, isAdmin } = useAuth();
  const [activePhase, setActivePhase] = useState<Phase>('transaction');
  const [transactionData, setTransactionData] = useState<TransactionData[]>([]);
  const [trendsData, setTrendsData] = useState<TransactionData[]>([]);
  const [trendsLoading, setTrendsLoading] = useState(false);
  const [trendsError, setTrendsError] = useState<string | null>(null);
  const [selectedMarketplace] = useState<MarketplaceCode>('US');
  // Hızlı Kâr ilk ziyarette yüklenir, sonra mount'ta kalır (girilen değerler sekme
  // değişince kaybolmasın). Ziyaret edilmezse hiç indirilmez — lazy() korunur.
  const [quickProfitVisited, setQuickProfitVisited] = useState(false);

  // Exclude sp-api-sync rows from profitability/transaction analyzers
  // Only Period Comparison (Trends) uses current month auto-synced data
  // Also drop marketplaces without a known USD conversion path (SE/MX/NL/PL/BE/JP/...)
  // — see currencyExchange.SUPPORTED_MARKETPLACE_CODES.
  const profitabilityData = useMemo(
    () => transactionData.filter(
      t => t.fileName !== 'sp-api-sync' && isSupportedMarketplace(t.marketplaceCode)
    ),
    [transactionData]
  );

  const analyzedTrendsData = useMemo(
    () => trendsData.filter(t => isSupportedMarketplace(t.marketplaceCode)),
    [trendsData]
  );

  // Surface the silent exclusion above: marketplaces with no USD conversion path
  // are dropped from profitability totals. Warn the user which/how many, so the
  // missing sales aren't mistaken for zero.
  const excludedMarketplaces = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const t of transactionData) {
      if (t.fileName === 'sp-api-sync') continue;
      if (!isSupportedMarketplace(t.marketplaceCode)) {
        const code = t.marketplaceCode || 'UNKNOWN';
        counts[code] = (counts[code] || 0) + 1;
      }
    }
    const codes = Object.keys(counts).sort();
    const total = codes.reduce((sum, c) => sum + counts[c], 0);
    return { codes, total };
  }, [transactionData]);

  // TransactionAnalyzer'dan data geldiğinde
  // TransactionAnalyzer sadece ALL data yüklendiğinde bunu çağırıyor (filtre değişikliklerinde değil)
  const handleDataLoaded = (data: TransactionData[]) => {
    // Always update - TransactionAnalyzer only calls this for full dataset loads
    setTransactionData(data);
  };

  useEffect(() => {
    if (activePhase === 'quickprofit') setQuickProfitVisited(true);
  }, [activePhase]);

  // Fetch exchange rates on app load (for all phases)
  useEffect(() => {
    fetchLiveRates().then(({ status }) => {
      if (status.error) {
        logger.warn('Exchange rate fetch warning:', status.error);
      } else {
        logger.log('Exchange rates loaded:', status.source);
      }
    });
  }, []);

  // Fetch trends data (canonical SP-API source) when admin enters Trends tab.
  // Lock with trendsError so a 500/429 doesn't trigger an infinite retry loop —
  // user must click "Tekrar Dene" to retry.
  useEffect(() => {
    if (activePhase !== 'trends' || !isAdmin) return;
    if (trendsData.length > 0 || trendsLoading || trendsError) return;

    setTrendsLoading(true);
    Promise.all([fetchTrendData(), fetchProductMapping()])
      .then(([trendResult, products]) => {
        const productMap = createProductMap(products);
        const mapped = trendResult.data
          .map(apiToTransactionData)
          .map(tx => enrichTransaction(tx, productMap));
        setTrendsData(mapped);
        logger.log(`Trends data loaded: ${mapped.length} rows, ${products.length} products`);
      })
      .catch(err => {
        const message = err instanceof Error ? err.message : 'Bilinmeyen hata';
        setTrendsError(message);
        logger.error('Failed to fetch trends data:', err);
      })
      .finally(() => setTrendsLoading(false));
  }, [activePhase, isAdmin, trendsData.length, trendsLoading, trendsError]);

  // Test Panel'den data geldiğinde (custom event listener)
  useEffect(() => {
    const handleTestDataLoad = (event: CustomEvent) => {
      const { data } = event.detail;
      setTransactionData(data);
      setActivePhase('transaction');
    };

    window.addEventListener('load-test-data', handleTestDataLoad as EventListener);

    return () => {
      window.removeEventListener('load-test-data', handleTestDataLoad as EventListener);
    };
  }, []);

  return (
    <div className="App">
      {/* Skip to main content link (visually hidden until focused) */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[100] focus:px-4 focus:py-2 focus:bg-blue-600 focus:text-white focus:rounded-lg focus:shadow-lg"
      >
        Skip to main content
      </a>
      {/* Navigation Header */}
      <nav className="bg-white shadow-md border-b border-slate-200 sticky top-0 z-50 no-print">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Tab Navigation */}
            <div className="flex gap-2">
              <button
                onClick={() => setActivePhase('transaction')}
                className={`px-6 py-3 rounded-lg font-semibold transition-all ${
                  activePhase === 'transaction'
                    ? 'bg-blue-600 text-white shadow-lg shadow-blue-200'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                Transaction Analyzer
              </button>

              <button
                onClick={() => setActivePhase('profitability')}
                disabled={transactionData.length === 0}
                className={`px-6 py-3 rounded-lg font-semibold transition-all ${
                  activePhase === 'profitability'
                    ? 'bg-purple-600 text-white shadow-lg shadow-purple-200'
                    : transactionData.length === 0
                    ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                Profitability Analyzer
              </button>

              {/* Hızlı Kâr — Excel'e bağlı DEĞİL, kendi verisini uçlardan çeker */}
              <button
                onClick={() => setActivePhase('quickprofit')}
                className={`px-6 py-3 rounded-lg font-semibold transition-all ${
                  activePhase === 'quickprofit'
                    ? 'bg-purple-600 text-white shadow-lg shadow-purple-200'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                Hızlı Kâr
              </button>

              {/* Trends - Admin Only (Beta) — independent of Excel data, uses canonical SP-API source */}
              {isAdmin && (
                <button
                  onClick={() => setActivePhase('trends')}
                  className={`px-6 py-3 rounded-lg font-semibold transition-all ${
                    activePhase === 'trends'
                      ? 'bg-cyan-600 text-white shadow-lg shadow-cyan-200'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                >
                  Trends Analyzer
                </button>
              )}
            </div>

            {/* Data Info & Actions */}
            <div className="flex items-center gap-4">
              {transactionData.length > 0 && (
                <div className="text-sm text-slate-600">
                  <span className="font-semibold text-slate-800">{transactionData.length}</span> transactions loaded
                </div>
              )}

              {/* User Menu */}
              <div className="flex items-center gap-3 pl-4 border-l border-slate-200">
                <div className="text-sm">
                  <span className="text-slate-600">Welcome, </span>
                  <span className="font-semibold text-slate-800">{user?.name}</span>
                  <span className={`ml-2 px-2 py-0.5 rounded text-xs ${
                    isAdmin ? 'bg-purple-100 text-purple-700' : 'bg-slate-100 text-slate-600'
                  }`}>
                    {user?.role}
                  </span>
                </div>
                <button
                  onClick={logout}
                  className="px-3 py-1.5 bg-red-50 text-red-600 rounded-lg text-sm font-medium hover:bg-red-100 transition-all"
                >
                  Logout
                </button>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Phase Components - Keep both mounted to preserve state */}
      <main id="main-content" className="min-h-screen">
        <div style={{ display: activePhase === 'transaction' ? 'block' : 'none' }}>
          <TransactionAnalyzer
            onDataLoaded={handleDataLoaded}
          />
        </div>

        <div style={{ display: activePhase === 'profitability' ? 'block' : 'none' }}>
          {transactionData.length > 0 ? (
            <Suspense fallback={<LoadingFallback />}>
              {excludedMarketplaces.codes.length > 0 && (
                <div className="max-w-7xl mx-auto px-6 pt-4">
                  <div className="flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                    <span className="text-lg leading-none">⚠️</span>
                    <div>
                      <span className="font-semibold">
                        {excludedMarketplaces.total} transaction{excludedMarketplaces.total === 1 ? '' : 's'} excluded
                      </span>{' '}
                      — no USD conversion path for: {excludedMarketplaces.codes.join(', ')}. These sales are not counted in the totals below.
                    </div>
                  </div>
                </div>
              )}
              <ProfitabilityAnalyzer
                transactionData={profitabilityData}
                selectedMarketplace={selectedMarketplace}
              />
            </Suspense>
          ) : (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100">
              <div className="bg-white rounded-2xl shadow-xl p-12 max-w-md text-center">
                <div className="text-6xl mb-6">💰</div>
                <h2 className="text-2xl font-bold text-slate-800 mb-3">
                  Transaction Data Required
                </h2>
                <p className="text-slate-600 mb-6">
                  Please upload your Excel files from Transaction Analyzer for Profitability Analysis.
                </p>
                <button
                  onClick={() => setActivePhase('transaction')}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold"
                >
                  Go to Transaction Analyzer
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Hızlı Kâr — kendi verisini çeker, Excel gerekmez */}
        {quickProfitVisited && (
          <div style={{ display: activePhase === 'quickprofit' ? 'block' : 'none' }}>
            <Suspense fallback={<LoadingFallback />}>
              <QuickProfit />
            </Suspense>
          </div>
        )}

        {/* Trends Analyzer - Admin Only (Beta) */}
        {isAdmin && (
          <div style={{ display: activePhase === 'trends' ? 'block' : 'none' }}>
            {trendsLoading ? (
              <LoadingFallback />
            ) : trendsData.length > 0 ? (
              <Suspense fallback={<LoadingFallback />}>
                <TrendsAnalyzer
                  transactionData={analyzedTrendsData}
                />
              </Suspense>
            ) : (
              <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100">
                <div className="bg-white rounded-2xl shadow-xl p-12 max-w-md text-center">
                  <div className="text-6xl mb-6">📈</div>
                  <h2 className="text-2xl font-bold text-slate-800 mb-3">
                    Trend verisi yüklenemedi
                  </h2>
                  <p className="text-slate-600 mb-6">
                    {trendsError || 'SP-API kanonik kaynaktan veri çekilemedi.'}
                  </p>
                  <button
                    onClick={() => { setTrendsError(null); setTrendsData([]); }}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold"
                  >
                    Tekrar Dene
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </main>

      {/* Test Panel - Only in Development Mode */}
      {process.env.NODE_ENV === 'development' && (
        <Suspense fallback={null}>
          <TestPanel onLoadTestData={(marketplace, data) => {
            logger.log(`Loading test data for ${marketplace}`);
            setTransactionData(data);
            setActivePhase('transaction');
          }} />
        </Suspense>
      )}
    </div>
  );
}

// App wrapper with AuthProvider
function App() {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingFallback />;
  }

  if (!isAuthenticated) {
    return <SSORedirect />;
  }

  return <AppContent />;
}

// Root component with AuthProvider and ToastProvider
function Root() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <ToastProvider>
          <App />
        </ToastProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default Root;
